// Cache em memoria por chave, com TTL opcional. Memoiza chamadas async
// (fetches) que retornam dados imutaveis-em-sessao — ex.: grupos de
// variaveis duma thing, rendimento hidraulico, total de variaveis.
//
// Diferenca pra redux-persist: este cache vive APENAS na memoria do
// browser (perde no F5). Bom pra dados que mudam entre sessoes mas nao
// dentro da mesma. Pra dados que devem sobreviver ao refresh, usar
// redux-persist (whitelist em store/index.js).
//
// Uso:
//   import { sessionCache } from '../../utils';
//   const groups = await sessionCache('variableGroups', thingId,
//     () => getVariableGroups(thingId));

const stores = new Map(); // namespace -> Map<key, { value, expiresAt }>

const DEFAULT_TTL_MS = 10 * 60 * 1000; // 10min — suficiente pra sessao

const getStore = (namespace) => {
  let s = stores.get(namespace);
  if (!s) {
    s = new Map();
    stores.set(namespace, s);
  }
  return s;
};

// Memoiza fetcher async por chave dentro do namespace. Se ja houver
// valor cacheado e ainda valido, retorna direto. Se houver promise
// in-flight com a mesma chave, retorna a mesma promise (dedupe — evita
// 2 cards disparem o mesmo fetch).
export const sessionCache = (namespace, key, fetcher, ttlMs = DEFAULT_TTL_MS) => {
  const store = getStore(namespace);
  const cached = store.get(key);
  const now = Date.now();
  if (cached) {
    // Promise in-flight — reaproveita
    if (cached.pending) return cached.pending;
    // Valor expirado — descarta
    if (cached.expiresAt && cached.expiresAt < now) {
      store.delete(key);
    } else {
      return Promise.resolve(cached.value);
    }
  }
  const pending = Promise.resolve()
    .then(() => fetcher())
    .then((value) => {
      store.set(key, {
        value,
        expiresAt: ttlMs ? now + ttlMs : null,
      });
      return value;
    })
    .catch((err) => {
      // Erro: nao cacheia, libera o slot pra retry
      store.delete(key);
      throw err;
    });
  store.set(key, { pending });
  return pending;
};

// Invalida uma entrada especifica (ex.: apos POST que muda os dados).
export const invalidateSessionCache = (namespace, key) => {
  const store = stores.get(namespace);
  if (store && key != null) store.delete(key);
};

// Limpa todo um namespace (ou tudo, se namespace nao for passado).
export const clearSessionCache = (namespace) => {
  if (namespace) {
    stores.delete(namespace);
  } else {
    stores.clear();
  }
};
